import { useRef } from 'react';

import { Box, Image } from '@chakra-ui/react';
import {
  EngagementNudgeData,
  EngagementNudgeDataProps,
  GoalsNudgeDataProps,
  KnowTeamNudgeDataProps,
  NudgeAction,
  NudgeCard,
  NudgeCardItem,
  RequestedFeedbackNudgeDataProps,
  ReviewsNudgeData,
  ReviewsNudgeDataProps,
  SharePraiseNudgeData,
  SharePraiseNudgeDataProps,
} from '@mesh.ai/rabbit';
import { useHistory } from 'react-router-dom';
import { checkOverviewExplanationsEnabled } from 'utils/helpers/featureFlags';

import EngagementIcon from 'assets/assetsV1/illustrations/engagementSurveyNudge.svg';
import GoalsCheckinIcon from 'assets/assetsV1/illustrations/goalsCheckinNudge.svg';
import KnowYourTeamIcon from 'assets/assetsV1/illustrations/knowYourTeamNudge.svg';
import NudgeCardGraphicIcon1 from 'assets/assetsV1/illustrations/nudgeCardGraphic1.svg';
import NudgeCardGraphicIcon2 from 'assets/assetsV1/illustrations/nudgeCardGraphic2.svg';
import NudgeCardGraphicIcon3 from 'assets/assetsV1/illustrations/nudgeCardGraphic3.svg';
import PendingReviewsIcon from 'assets/assetsV1/illustrations/pendingReviewsNudge.svg';
import SharePraiseIcon from 'assets/assetsV1/illustrations/sharePraiseNudge.svg';

import { getRandomIntBetween } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendMeshRecommendsClickedEvent } from 'constants/mixpanelHelper';

import KebabMenu from './KebabMenu';
import { GoalCheckinPendingNudge } from './components/GoalCheckinPendingNudge';
import { IntelligentNudge } from './components/IntelligentNudge';
import { KnowYourTeamNudge } from './components/KnowYourTeamNudge';
import { RequestedFeedbackNudge } from './components/RequestedFeedbackNudge';
import { SharePraiseNudge } from './components/SharePraiseNudge';
import { TeamPromotionAcknowledgeButton } from './components/TeamPromotionAcknowledgeButton';
import { mapNudgeDataToProps } from './helpers/mapNudgeDataToProps';
import { handleActionClick, openInNewTab, openInSameTab } from './helpers/nudgeActionHelpers';
import { InformationalNudgeDataProps } from './types/informational';
import type { OverviewExplanation } from './types/overviewExplanation';
import type { TeamPromotionAcknowledge } from './types/teamPromotionAcknowledge';

export const NudgeCardWrapper = ({ nudgeCards }: { nudgeCards: ReturnType<typeof mapNudgeDataToProps> }) => {
  const history = useHistory();

  const icons = [NudgeCardGraphicIcon1, NudgeCardGraphicIcon2, NudgeCardGraphicIcon3];
  const iconMapRef = useRef<{ [ecaId: number]: string }>({});

  const getRandomIcon = (ecaId: number) => {
    if (!iconMapRef.current[ecaId]) {
      const randomIndex = getRandomIntBetween(0, icons.length - 1);
      iconMapRef.current[ecaId] = icons[randomIndex] as unknown as string;
    }
    return iconMapRef.current[ecaId];
  };

  return (
    <>
      {nudgeCards.map((nudgeCard) => {
        switch (nudgeCard.uiTemplate) {
          case 'REVIEW':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={PendingReviewsIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <NudgeCardItem key={nc.nudgeId}>
                    <ReviewsNudgeData {...(nc as ReviewsNudgeDataProps)} />
                    <NudgeAction
                      onClick={() =>
                        handleActionClick({
                          ecaId: nudgeCard.ecaId,
                          uiTemplate: nudgeCard.uiTemplate,
                          action: () => openInNewTab(nc.buttonLink),
                        })
                      }
                    >
                      {nc.buttonText}
                    </NudgeAction>
                  </NudgeCardItem>
                ))}
              />
            );
          case 'GOAL_CHECKIN_PENDING':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={GoalsCheckinIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <GoalCheckinPendingNudge
                    key={nc.nudgeId}
                    buttonLink={nc.buttonLink}
                    buttonText={nc.buttonText}
                    nudgeCard={{
                      ecaId: nudgeCard.ecaId,
                      uiTemplate: nudgeCard.uiTemplate,
                    }}
                    {...(nc as GoalsNudgeDataProps)}
                  />
                ))}
              />
            );
          case 'SHARE_PRAISE':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={SharePraiseIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <SharePraiseNudge
                    {...(nc as SharePraiseNudgeDataProps)}
                    buttonLink={nc.buttonLink}
                    buttonText={nc.buttonText}
                    nudgeCard={nudgeCard}
                  />
                ))}
              />
            );
          case 'SHARE_ADVICE':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={SharePraiseIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <NudgeCardItem key={nc.nudgeId}>
                    <SharePraiseNudgeData {...(nc as SharePraiseNudgeDataProps)} />
                    <NudgeAction
                      onClick={() =>
                        handleActionClick({
                          ecaId: nudgeCard.ecaId,
                          uiTemplate: nudgeCard.uiTemplate,
                          action: () => openInSameTab(history, nc.buttonLink),
                        })
                      }
                    >
                      {nc.buttonText}
                    </NudgeAction>
                  </NudgeCardItem>
                ))}
              />
            );
          case 'FEEDBACK':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={SharePraiseIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <NudgeCardItem key={nc.nudgeId}>
                    <SharePraiseNudgeData {...(nc as SharePraiseNudgeDataProps)} />
                    <NudgeAction
                      onClick={() =>
                        handleActionClick({
                          ecaId: nudgeCard.ecaId,
                          uiTemplate: nudgeCard.uiTemplate,
                          action: () => openInSameTab(history, nc.buttonLink),
                        })
                      }
                    >
                      {nc.buttonText}
                    </NudgeAction>
                  </NudgeCardItem>
                ))}
              />
            );
          case 'REQUESTED_FEEDBACK':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={SharePraiseIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <RequestedFeedbackNudge
                    {...(nc as RequestedFeedbackNudgeDataProps)}
                    buttonLink={nc.buttonLink}
                    buttonText={nc.buttonText}
                    nudgeCard={nudgeCard}
                  />
                ))}
              />
            );
          case 'KNOW_YOUR_TEAM':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={KnowYourTeamIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <KnowYourTeamNudge
                    buttonLink={nc.buttonLink}
                    buttonText={nc.buttonText}
                    key={nc.nudgeId}
                    nudgeCard={{
                      ecaId: nudgeCard.ecaId,
                      uiTemplate: nudgeCard.uiTemplate,
                    }}
                    {...(nc as KnowTeamNudgeDataProps)}
                  />
                ))}
              />
            );
          case 'ENGAGEMENT':
            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={EngagementIcon as unknown as string}
                kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                pageSize={2}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => (
                  <NudgeCardItem key={nc.nudgeId}>
                    <EngagementNudgeData {...(nc as EngagementNudgeDataProps)} />
                    <NudgeAction
                      onClick={() =>
                        handleActionClick({
                          ecaId: nudgeCard.ecaId,
                          uiTemplate: nudgeCard.uiTemplate,
                          action: () => openInNewTab(nc.buttonLink),
                        })
                      }
                    >
                      {nc.buttonText}
                    </NudgeAction>
                  </NudgeCardItem>
                ))}
              />
            );
          case 'INFORMATIONAL':
            return (
              <Box
                sx={{
                  '.mt-1.flex.items-center.gap-4': {
                    width: '100%',
                    '@media (min-width: 1600px)': {
                      maxWidth: '70%',
                    },
                  },
                  '.mt-1.flex.items-center.gap-4 > .flex.flex-col': {
                    width: '100%',
                  },
                  '.mt-1.flex.items-center.gap-4 > .flex.flex-col > .flex.items-center.rounded-lg': {
                    border: 'none',
                    padding: '0',
                  },
                }}
              >
                <NudgeCard
                  key={nudgeCard.ecaId}
                  title=""
                  description=""
                  imgSrc=""
                  kebabMenu={<KebabMenu ecaId={nudgeCard.ecaId} uiTemplate={nudgeCard.uiTemplate} />}
                  pageSize={2}
                  variant="informational"
                  nudgeCardList={nudgeCard.data.nudgeContent.map((nc) => {
                    const informationalContent = nc as InformationalNudgeDataProps;
                    return (
                      <NudgeCardItem key={nc.nudgeId}>
                        <Image
                          src={isMobileView() ? informationalContent.imgSmall : informationalContent.imgBig}
                          fallbackSrc={isMobileView() ? informationalContent.imgBig : informationalContent.imgSmall}
                          onClick={() => {
                            sendMeshRecommendsClickedEvent({
                              eventProperties: {
                                ecaId: nudgeCard.ecaId,
                                source: 'HOME_PAGE',
                                type: nudgeCard.uiTemplate,
                              },
                            });
                            openInNewTab(informationalContent.imgClickLink);
                          }}
                          width="100%"
                          cursor="pointer"
                        />
                      </NudgeCardItem>
                    );
                  })}
                />
              </Box>
            );
          case 'OVERVIEW_EXPLANATION': {
            if (!checkOverviewExplanationsEnabled()) return null;
            const randomIcon = getRandomIcon(nudgeCard.ecaId);

            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title=""
                description=""
                imgSrc={randomIcon as unknown as string}
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc: OverviewExplanation) => (
                  <IntelligentNudge
                    key={nc.nudgeId}
                    header={nc.actionItemData}
                    explanation={nc.explanation}
                    initiativeTitle={nc.actionItemData}
                    buttonText={nc.buttonText}
                  />
                ))}
                pageSize={2}
                variant="informational"
                kebabMenu={
                  <KebabMenu
                    ecaId={nudgeCard.ecaId}
                    uiTemplate={nudgeCard.uiTemplate}
                    nudgeIdType="NUDGE_ID"
                    nudgeId={nudgeCard?.data?.nudgeContent?.[0]?.nudgeId}
                  />
                }
              />
            );
          }
          case 'TEAM_PROMOTION_ACKNOWLEDGE': {
            const randomIcon = getRandomIcon(nudgeCard.ecaId);

            return (
              <NudgeCard
                key={nudgeCard.ecaId}
                title={nudgeCard.data.header}
                description={nudgeCard.data.subTitle}
                imgSrc={randomIcon as unknown as string}
                kebabMenu={
                  <KebabMenu
                    ecaId={nudgeCard.ecaId}
                    uiTemplate={nudgeCard.uiTemplate}
                    nudgeIdType="NUDGE_ID"
                    nudgeId={nudgeCard?.data?.nudgeContent?.[0]?.nudgeId}
                  />
                }
                nudgeCardList={nudgeCard.data.nudgeContent.map((nc: TeamPromotionAcknowledge) => (
                  <TeamPromotionAcknowledgeButton
                    key={nc.nudgeId}
                    ecaId={nudgeCard.ecaId}
                    nudgeId={nc.nudgeId}
                    buttonText={nc.buttonText}
                    uiTemplate={nudgeCard.uiTemplate}
                  />
                ))}
              />
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
};
